/** @file Colour field: swatch picker plus hex text, and an alpha number for #rrggbbaa values. */
import { useState } from 'react';
import './tokens.css';
import './ColorField.css';
import { NumberField } from './NumberField';
import { TextInput } from './TextInput';
import { hexColor } from './logic/hexColor';

/** Props of ColorField. */
export interface ColorFieldProps {
  /** `#rrggbbaa` when `alpha`, else `#rrggbb`. */
  value: string;
  alpha: boolean;
  /** Already-translated accessible names of the hex input, the swatch and the alpha number. */
  labels: { hex: string; pick: string; alpha: string };
  /** Called once per finished edit with a valid lower-case colour that differs from `value`. */
  onCommit: (next: string) => void;
  disabled?: boolean;
}

/** The swatch previews while picking and commits on blur; typed hex commits on Enter or blur when valid. */
export function ColorField({ value, alpha, labels, onCommit, disabled = false }: ColorFieldProps) {
  const [picking, setPicking] = useState<string | null>(null);
  const valid = hexColor.isValid(value, alpha);
  const rgb = valid ? hexColor.rgb(value) : '#000000';
  const a = valid ? hexColor.alpha(value) : 1;
  const commit = (next: string): void => {
    if (hexColor.isValid(next, alpha) && next.toLowerCase() !== value.toLowerCase()) onCommit(next.toLowerCase());
  };
  return (
    <span className="p2-color">
      <input type="color" className="p2-color-swatch" aria-label={labels.pick} value={picking ?? rgb} disabled={disabled}
        onChange={(e) => { setPicking(e.currentTarget.value); }}
        onBlur={() => { if (picking !== null) commit(hexColor.join(picking, a, alpha)); setPicking(null); }} />
      <TextInput className="p2-color-hex" label={labels.hex} value={value} disabled={disabled} onCommit={commit} />
      {alpha && <NumberField label={labels.alpha} value={a} min={0} max={1} step={0.01} precision={3} disabled={disabled} onCommit={(next) => { commit(hexColor.join(rgb, next, true)); }} />}
    </span>
  );
}
